<template>
  <div class="news-app">
    <Preloader :backgroundColor="preloaderColor"></Preloader>

    <!-- Modal de contenido compartido -->
    <SharedModal
      :show="showSharedModal"
      :item="sharedItem"
      @close="closeSharedModal"
    />

    <!-- Header -->
    <HeaderNews
      subtitle="Noticias y contenido sobre Inteligencia Artificial"
      :showSearch="true"
      :searchQuery.sync="query"
      :showMobileMenu="true"
      @toggle-mobile-menu="toggleMobileMenu"
    />

    <!-- Main Layout -->
    <div class="news-layout">
      <!-- Sidebar -->
      <Sidebar :class="{'sidebar--mobile-visible': showMobileSidebar}" @navigate="showMobileSidebar = false" @toggle-collapse="handleSidebarCollapse" />

      <!-- Mobile Overlay -->
      <div
        v-if="showMobileSidebar"
        class="mobile-overlay"
        @click="toggleMobileMenu"
      ></div>

      <!-- Main Content -->
      <div class="news-body">
      <main class="news-main" ref="newsMain">
        <div class="news-main__container">
        <!-- Encabezado + filtros fijos -->
        <div class="section-header-fixed">
          <div class="section-header">
            <div class="section-header__icon">
              <img :src="currentSection.icon" :alt="currentSection.label" width="24" height="24">
            </div>
            <div class="section-header__main">
              <div class="section-header__top">
                <h1 class="section-title">{{ currentSection.label }}</h1>
                <span v-if="metadata" class="section-count">{{ currentSectionCount }} items</span>
              </div>
              <p class="section-description">{{ currentSection.subtitle }}</p>
            </div>
            <!-- Fila de botones de filtro -->
            <div v-if="availableCategories.length > 0 || activeTab === 'pappers'" class="filter-actions-row">
              <!-- Botón para mostrar/ocultar filtros -->
              <button
                v-if="availableCategories.length > 0"
                @click="showFilters = !showFilters"
                class="filters-toggle-btn"
                :title="selectedCategoryLabel"
                :aria-label="selectedCategoryLabel"
                :class="{'filters-toggle-btn--active': showFilters || (selectedCategory && selectedCategory !== 'Featured')}"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="4" y1="6" x2="20" y2="6"/>
                  <line x1="4" y1="12" x2="20" y2="12"/>
                  <line x1="4" y1="18" x2="20" y2="18"/>
                  <circle cx="8" cy="6" r="2" fill="currentColor"/>
                  <circle cx="16" cy="12" r="2" fill="currentColor"/>
                  <circle cx="12" cy="18" r="2" fill="currentColor"/>
                </svg>
                <span>{{ selectedCategoryLabel }}</span>
              </button>

              <!-- Botón de destacados (por ahora solo en Papers, usa la categoría prioritaria "Featured") -->
              <button
                v-if="activeTab === 'pappers'"
                @click="toggleCategory('Featured')"
                class="filters-toggle-btn featured-btn"
                title="Destacados"
                aria-label="Destacados"
                :class="{'featured-btn--active': selectedCategory === 'Featured'}"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="8" r="6" :fill="selectedCategory === 'Featured' ? 'currentColor' : 'none'"/>
                  <path d="M15.48 12.89L17 22l-5-3-5 3 1.52-9.11"/>
                </svg>
                <span>Destacados</span>
              </button>
            </div>
          </div>

          <!-- Filtros de categorías -->
          <div
            v-if="availableCategories.length > 0"
            class="categories-filter"
            :class="{'categories-filter--visible': showFilters}"
          >
            <div class="categories-tags">
              <button
                v-for="cat in availableCategories"
                :key="cat.category"
                :class="['category-tag', {active: isCategorySelected(cat.category)}]"
                @click="toggleCategory(cat.category)"
              >
                <span class="category-tag-label">{{ cat.title }}</span>
                <span class="category-tag-count">{{ cat.count }}</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Content scrollable -->
        <section class="news-content" ref="scrollContainer">
          <!-- Error Message -->
          <div v-if="error" class="error-message">
            <div class="error-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="15" y1="9" x2="9" y2="15"/>
                <line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
            </div>
            <div class="error-content">
              <h4>Error al cargar noticias</h4>
              <p>{{ error }}</p>
            </div>
          </div>

          <!-- Líderes en IA: accesos rápidos (solo iconos) sobre las noticias -->
          <nav v-if="activeTab === 'notice'" class="company-tags" aria-label="Líderes en IA">
            <span class="company-tags__label">🔥 Líderes en IA</span>
            <div class="company-tags__list">
              <!-- Aro de color = la empresa tiene destacado en companyHighlights.config.js -->
              <router-link
                v-for="company in topCompanies"
                :key="company.slug"
                :to="`/news/top-companies/${company.slug}`"
                class="company-tag"
                :class="{'company-tag--highlight': companyHighlights[company.slug]}"
                :aria-label="company.name"
              >
                <img :src="company.image" alt="" width="32" height="32">
              </router-link>
            </div>
          </nav>

          <!-- Feed List -->
          <FeedList :items="filteredItems" :loading="initialLoading" :layout="currentSection.layout">
            <!-- Noticias de última hora: solo en la sección Noticias -->
            <template #pinned>
              <BreakingNewsCard v-if="activeTab === 'notice'" />
            </template>
          </FeedList>

          <!-- Loading más items -->
          <div v-if="loadingMore && !initialLoading" class="loading-more">
            <div class="loading-spinner"></div>
            <span>Cargando más contenido...</span>
          </div>

          <!-- Botón Cargar Más -->
          <div v-if="!loadingMore && !initialLoading && currentChunk < totalChunks && visible.length > 0" class="load-more-container">
            <button @click="loadChunk" class="load-more-btn">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="6 9 12 15 18 9"/>
              </svg>
              <span>Cargar más contenido ({{ currentChunk }}/{{ totalChunks }})</span>
            </button>
          </div>

          <!-- Fin de resultados -->
          <div v-if="!loadingMore && currentChunk >= totalChunks && visible.length > 0" class="end-message">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            <span>Has visto todo el contenido disponible</span>
          </div>
        </section>
        </div>
      </main>

      <!-- Footer -->
      <Footer />
      </div>
    </div>

    <!-- Botón scroll to top -->
    <button
      @click="scrollToTop"
      class="scroll-to-top"
      :class="{'scroll-to-top--visible': showScrollTop}"
      aria-label="Volver al inicio"
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <polyline points="18 15 12 9 6 15"/>
      </svg>
    </button>
  </div>
</template>

<script>
import HeaderNews from '../components/HeaderNews';
import Sidebar from '../components/Sidebar';
import FeedList from '../components/FeedList';
import Footer from '../components/Footer';
import Preloader from '../../../components/Preloader';
import SharedModal from '../components/SharedModal';
import BreakingNewsCard from '../components/BreakingNewsCard';
import { decodeShareData, fetchSharedItem, generateMetaTags, applyMetaTags } from '../../../utils/shareUtils';
import feedChunksConfig from '../../../config/feedChunks.config';
import topCompanies from '../../../config/topCompanies.config';
import companyHighlights from '../../../config/companyHighlights.config';

export default {
  name: 'NewsIA',
  components: {
    HeaderNews,
    Sidebar,
    FeedList,
    Footer,
    Preloader,
    SharedModal,
    BreakingNewsCard
  },
  data() {
    return {
      // Datos de chunks
      loadedItems: [],           // Todos los items cargados
      currentChunk: 0,            // Chunk actual
      totalChunks: 0,             // Total de chunks disponibles

      // Estados de carga
      initialLoading: false,      // Primera carga
      loadingMore: false,         // Cargando más chunks
      loading: false,             // Loading general (compatibilidad)
      error: '',

      // Filtros
      query: '',
      activeTab: this.$route.meta.sourceType || 'notice', // Tipo de contenido (viene de la ruta)
      topCompanies,
      companyHighlights,
      metadata: null,             // Metadata completa del feed

      // Config estática por sección (icono, label, subtítulo, layout de FeedList)
      sectionsConfig: {
        notice: { label: 'Noticias', icon: '/assets/icons/news.png', subtitle: 'Actualidad sobre Inteligencia Artificial', layout: 'grid' },
        youtube: { label: 'Videos', icon: '/assets/icons/youtube.png', subtitle: 'Canales y creadores de contenido en IA', layout: 'grid' },
        educational: { label: 'Educación', icon: '/assets/icons/educational.png', subtitle: 'Recursos y artículos para aprender sobre IA', layout: 'grid' },
        forum: { label: 'Foros', icon: '/assets/icons/forum.png', subtitle: 'Discusiones de la comunidad', layout: 'grid' },
        pappers: { label: 'Papers', icon: '/assets/icons/papers.png', subtitle: 'Papers académicos — lee el PDF sin salir del sitio', layout: 'list' }
      },
      selectedCategory: null,     // Categoría seleccionada para filtrar (una sola a la vez)

      // Scroll to top
      showScrollTop: false,       // Mostrar botón de scroll to top

      // Filtros (mobile)
      showFilters: false,         // Controla visibilidad de filtros en mobile

      // Responsive
      windowWidth: window.innerWidth,

      // Mobile sidebar
      showMobileSidebar: false,

      preloaderColor: 'rgba(14, 18, 23, 0.8)', // Color de fondo del preloader

      // Modal de contenido compartido
      showSharedModal: false,
      sharedItem: null,
    };
  },
  computed: {
    // Config de la sección actual (icono, label, subtítulo, layout)
    currentSection() {
      return this.sectionsConfig[this.activeTab] || this.sectionsConfig.notice;
    },

    // Total de items del tipo activo, según metadata (formateado con separador de miles)
    currentSectionCount() {
      const total = this.metadata?.byType?.[this.activeTab]?.totalItems || 0;
      return total.toLocaleString('es');
    },

    // Texto del botón de filtros: nombre de la categoría activa o label genérico
    selectedCategoryLabel() {
      if (!this.selectedCategory) return 'Filtrar por fuente';
      const cat = this.availableCategories.find(c => c.category === this.selectedCategory);
      return cat ? cat.title : 'Filtrar por fuente';
    },

    // Categorías disponibles para el tab activo
    availableCategories() {
      if (!this.metadata || !this.metadata.categories || !this.metadata.categories[this.activeTab]) {
        return [];
      }

      // Lista de categorías con secciones independientes (excluir de tags)
      const excludedCategories = [
        'HuggingFace',   // Tiene sección propia en /news/huggingface
        'Featured',      // Tiene su propio botón "Destacados"
        ...topCompanies.map(c => c.category) // Líderes en IA: /news/top-companies/:slug
      ];

      // Filtrar categorías excluidas y ordenar alfabéticamente por el título
      return [...this.metadata.categories[this.activeTab]]
        .filter(cat => !excludedCategories.includes(cat.category))
        .sort((a, b) => {
          return a.title.localeCompare(b.title, 'es', { sensitivity: 'base' });
        });
    },

    // Detectar si es mobile
    isMobile() {
      return this.windowWidth < 768;
    },

    // Items filtrados por búsqueda
    filteredItems() {
      let filtered = this.loadedItems;

      // Filtro por texto
      const q = this.query.trim().toLowerCase();
      if (q) {
        filtered = filtered.filter(it =>
          (it.title && it.title.toLowerCase().includes(q)) ||
          (it.description && it.description.toLowerCase().includes(q))
        );
      }

      return filtered;
    },

    // Backward compatibility (mantener 'visible' para no romper código existente)
    visible() {
      return this.filteredItems;
    }
  },
  methods: {
    async loadMetadata() {
      try {
        const metadataUrl = `${feedChunksConfig.baseUrl}metadata.json`;
        const response = await fetch(metadataUrl);
        if (!response.ok) throw new Error('No se pudo cargar metadata');
        this.metadata = await response.json();

        // Establecer total de chunks según el tipo activo
        if (this.metadata.byType && this.metadata.byType[this.activeTab]) {
          this.totalChunks = this.metadata.byType[this.activeTab].totalChunks;
          console.log(`📊 Metadata cargada: ${this.totalChunks} chunks disponibles para ${this.activeTab}`);
        }
      } catch (e) {
        console.error('Error cargando metadata:', e);
        throw e;
      }
    },
    
    async loadChunk() {
      if (this.loadingMore || this.currentChunk >= this.totalChunks) {
        console.log(`⏸️ No se carga chunk: loadingMore=${this.loadingMore}, currentChunk=${this.currentChunk}, totalChunks=${this.totalChunks}`);
        return;
      }

      const isFirstLoad = this.currentChunk === 0;
      if (isFirstLoad) {
        this.initialLoading = true;
      } else {
        this.loadingMore = true;
      }

      this.error = '';

      try {
        let items = [];

        // 🎯 LÓGICA HÍBRIDA DE CARGA (UN SOLO FILTRO)
        if (!this.selectedCategory) {
          // Sin filtros: cargar chunks "all"
          const chunkFile = `${this.activeTab}-all-chunk-${this.currentChunk}.json`;
          const chunkUrl = `${feedChunksConfig.baseUrl}${chunkFile}`;
          const response = await fetch(chunkUrl);
          if (!response.ok) throw new Error(`No se pudo cargar ${chunkFile}`);
          items = await response.json();
          console.log(`✅ Cargado: ${chunkFile}`);
        } else {
          // Con filtro: cargar chunk de la categoría seleccionada
          const categoriesMetadata = this.metadata.categories[this.activeTab] || [];
          const categoryMeta = categoriesMetadata.find(c => c.category === this.selectedCategory);

          if (categoryMeta && categoryMeta.isPriority && categoryMeta.totalChunks > 0) {
            // Categoría prioritaria: tiene chunks propios
            if (this.currentChunk < categoryMeta.totalChunks) {
              const chunkFile = `${categoryMeta.chunkPrefix}-chunk-${this.currentChunk}.json`;
              const chunkUrl = `${feedChunksConfig.baseUrl}${chunkFile}`;
              const response = await fetch(chunkUrl);
              if (response.ok) {
                items = await response.json();
                console.log(`✅ Cargado: ${chunkFile} (${items.length} items)`);
              } else {
                console.warn(`⚠️ No se pudo cargar ${chunkFile}`);
                // Si no se puede cargar el chunk, detenemos la carga
                return;
              }
            } else {
              // Ya no hay más chunks de esta categoría
              console.log(`ℹ️ No hay más chunks para ${this.selectedCategory} (${this.currentChunk}/${categoryMeta.totalChunks})`);
              return;
            }
          } else {
            // Categoría no prioritaria: filtrar del "all"
            const chunkFile = `${this.activeTab}-all-chunk-${this.currentChunk}.json`;
            const chunkUrl = `${feedChunksConfig.baseUrl}${chunkFile}`;
            const response = await fetch(chunkUrl);
            if (response.ok) {
              const allItems = await response.json();
              items = allItems.filter(item => item.sourceCategory === this.selectedCategory);
              console.log(`✅ Cargado y filtrado: ${chunkFile} (${items.length}/${allItems.length} items de ${this.selectedCategory})`);
            } else {
              console.warn(`⚠️ No se pudo cargar ${chunkFile}`);
              return;
            }
          }
        }

        // Transformar items al formato esperado por tu app
        const transformedItems = items.map(item => {
          return {
            id: item.id,
            title: item.title || '',
            link: item.link || '',
            pubDate: item.pubDate ? new Date(item.pubDate) : null,
            description: item.description || '',
            category: item.sourceCategory || 'Sin categoría',
            sourceCategory: item.sourceCategory || 'Sin categoría',
            sourceType: item.sourceType || 'Otro',
            sourceTitle: item.sourceTitle || '',
            sourceCountry: item.sourceCountry || '',
            author: item.author || 'Desconocido',
            content: item.content || '',
            image: item.image || null, // 🆕 Incluir campo image para thumbnails
            isShortVideo: item.isShortVideo || 0,
            linkPdf: item.linkPdf || ''
          };
        });

        // Agregar nuevos items
        this.loadedItems.push(...transformedItems);

        // IMPORTANTE: Remover duplicados después de agregar
        // Usar Map para mantener solo la primera ocurrencia de cada link
        // const uniqueItemsMap = new Map();
        // this.loadedItems.forEach(item => {
        //   if (!uniqueItemsMap.has(item.id)) {
        //     uniqueItemsMap.set(item.id, item);
        //   }
        // });

        // Reemplazar el array con items únicos
        //const beforeCount = this.loadedItems.length;
        //this.loadedItems = Array.from(uniqueItemsMap.values());
        //const afterCount = this.loadedItems.length;
        //const duplicatesRemoved = beforeCount - afterCount;

        this.currentChunk++;

        //console.log(`✅ ${this.activeTab}-chunk-${this.currentChunk - 1} cargado (${items.length} items, ${duplicatesRemoved} duplicados removidos). Total único: ${this.loadedItems.length}`);

      } catch (e) {
        this.error = e.message || 'Error desconocido al cargar noticias';
        console.error('Error en loadChunk:', e);
      } finally {
        this.initialLoading = false;
        this.loadingMore = false;
        this.loading = false;
      }
    },

    // Método para cambiar de sección (disparado por navegación en el sidebar)
    async changeTab(newTab) {
      if (newTab === this.activeTab) return;

      console.log(`🔄 Cambiando sección a: ${newTab}`);

      // Activar loading INMEDIATAMENTE
      this.initialLoading = true;

      // Limpiar items ANTES de cambiar (forzar re-render limpio)
      this.loadedItems = [];

      // Esperar un tick para que Vue procese la limpieza
      await this.$nextTick();

      // Actualizar tab activo
      this.activeTab = newTab;

      // Reset completo del estado
      this.currentChunk = 0;
      this.error = '';
      this.selectedCategory = null; // Reset filtro de categoría (incluye "Destacados")

      // Actualizar totalChunks según el nuevo tab
      if (this.metadata?.byType?.[newTab]) {
        this.totalChunks = this.metadata.byType[newTab].totalChunks;
      }

      // Scroll al inicio
      if (this.$refs.newsMain) {
        this.$refs.newsMain.scrollTop = 0;
      }

      // Cargar primer chunk del nuevo tab
      await this.loadChunk();
    },

    // Toggle categoría en filtros (solo una a la vez)
    async toggleCategory(category) {
      // Activar loading INMEDIATAMENTE para limpiar la vista
      this.initialLoading = true;

      // Limpiar items ANTES de cambiar el filtro (esto fuerza un re-render)
      this.loadedItems = [];

      // Esperar un tick para que Vue procese la limpieza
      await this.$nextTick();

      // Si ya está seleccionada, deseleccionarla
      if (this.selectedCategory === category) {
        this.selectedCategory = null;
        console.log('🔄 Filtro desactivado - mostrando todos');
        // Restaurar totalChunks del tipo completo
        if (this.metadata?.byType?.[this.activeTab]) {
          this.totalChunks = this.metadata.byType[this.activeTab].totalChunks;
        }
      } else {
        // Seleccionar nueva categoría (desmarca la anterior automáticamente)
        this.selectedCategory = category;
        console.log(`🔄 Filtro activado: ${category}`);

        // Actualizar totalChunks según la categoría seleccionada
        const categoriesMetadata = this.metadata.categories[this.activeTab] || [];
        const categoryMeta = categoriesMetadata.find(c => c.category === category);

        if (categoryMeta && categoryMeta.isPriority && categoryMeta.totalChunks > 0) {
          this.totalChunks = categoryMeta.totalChunks;
        } else {
          // Para categorías no prioritarias, usar los chunks del tipo completo
          this.totalChunks = this.metadata.byType[this.activeTab].totalChunks;
        }
      }

      // Reset del chunk counter
      this.currentChunk = 0;

      // Scroll al inicio
      if (this.$refs.newsMain) {
        this.$refs.newsMain.scrollTop = 0;
      }

      // Cargar primer chunk con filtros aplicados
      await this.loadChunk();
    },

    // Verificar si una categoría está seleccionada
    isCategorySelected(category) {
      return this.selectedCategory === category;
    },

    handleScroll(event) {
      const element = event.target;
      const scrollTop = element.scrollTop;
      this.showScrollTop = scrollTop > 50; // Mostrar después de 50px de scroll
    },

    scrollToTop() {
      if (this.$refs.newsMain) {
        this.$refs.newsMain.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
    },

    handleResize() {
      this.windowWidth = window.innerWidth;
      // Cerrar sidebar móvil al cambiar a desktop
      if (this.windowWidth >= 768) {
        this.showMobileSidebar = false;
      }
    },

    toggleMobileMenu() {
      this.showMobileSidebar = !this.showMobileSidebar;
    },

    handleSidebarCollapse(isCollapsed) {
      // Manejar el colapso del sidebar si es necesario
      console.log('Sidebar collapsed:', isCollapsed);
    },

    async refresh() {
      console.log('🔄 Refrescando feeds...');

      // Reset estado
      this.loadedItems = [];
      this.currentChunk = 0;
      this.totalChunks = 0;
      this.error = '';
      this.loading = true;
      this.initialLoading = true;

      try {
        // Cargar metadata primero
        await this.loadMetadata();

        // Cargar primer chunk
        await this.loadChunk();

      } catch (e) {
        this.error = e.message || 'Error al inicializar feeds';
        console.error('Error en refresh:', e);
      } finally {
        this.loading = false;
        this.initialLoading = false;
      }
    },

    // Detectar y manejar contenido compartido
    async handleSharedContent() {
      // Verificar si es una ruta compartida
      if (this.$route.meta.isShared && this.$route.params.shareId) {
        const shareId = this.$route.params.shareId;
        console.log('🔗 Detectado contenido compartido:', shareId);

        // Enlace corto (/news/s/:id) o enlace largo antiguo con el item comprimido
        const decodedItem = this.$route.name === 'news-short-share'
          ? await fetchSharedItem(shareId)
          : decodeShareData(shareId);

        if (decodedItem) {
          console.log('✅ Contenido decodificado:', decodedItem);

          // Guardar item y mostrar modal
          this.sharedItem = decodedItem;
          this.showSharedModal = true;

          // Actualizar meta tags para redes sociales
          const metaTags = generateMetaTags(decodedItem);
          applyMetaTags(metaTags);

          // Actualizar título del documento
          document.title = `${decodedItem.title} | Devlusaja Noticias IA`;
        } else {
          // Ya salió de los feed-chunks (o enlace inválido): se muestra el feed normal
          console.warn('Contenido compartido no encontrado');
          this.$router.replace('/news');
        }
      }
    },

    // Cerrar modal compartido
    closeSharedModal() {
      this.showSharedModal = false;
      this.sharedItem = null;

      // Navegar de vuelta a la ruta principal
      if (this.$route.meta.isShared) {
        this.$router.push('/news');
      }
    },
    
  },
  
  async mounted() {
    // Detectar si hay contenido compartido primero
    this.handleSharedContent();

    // Siempre cargar feeds en background (incluso si es shared)
    // Esto permite que al cerrar el modal, los feeds ya estén cargados
    await this.refresh();

    // Esperar a que el DOM esté completamente renderizado
    await this.$nextTick();
    await this.$nextTick();
    await this.$nextTick();

    // Agregar listener para resize
    window.addEventListener('resize', this.handleResize);

    // Asegurar que newsMain esté disponible
    setTimeout(() => {
      const newsMain = this.$refs.newsMain;
      if (newsMain) {
        // Agregar listener de scroll
        newsMain.addEventListener('scroll', this.handleScroll, { passive: true });
      }
    }, 500);
  },

  // Watch para detectar cambios de ruta
  watch: {
    async '$route'(to, from) {
      // Cambio de sección (Noticias/Papers/Educación/Foros/Videos) vía sidebar
      if (to.meta.sourceType && to.meta.sourceType !== from.meta.sourceType) {
        await this.changeTab(to.meta.sourceType);
        return;
      }

      // Si navegamos a una ruta compartida
      if (to.meta.isShared && to.params.shareId) {
        this.handleSharedContent();
      }
      // Si salimos de una ruta compartida
      else if (from.meta.isShared && !to.meta.isShared) {
        this.closeSharedModal();

        // Si no hay feeds cargados, cargarlos
        if (this.loadedItems.length === 0 && !this.loading) {
          await this.refresh();
        }
      }
    }
  },

  beforeUnmount() {
    // Remover listener de scroll
    const newsMain = this.$refs.newsMain;
    if (newsMain) {
      newsMain.removeEventListener('scroll', this.handleScroll);
    }

    // Remover listener de resize
    window.removeEventListener('resize', this.handleResize);
  }
};
</script>

<style scoped>
/* Dark Theme - daily.dev style */
.news-app {
  min-height: 100vh;
  background: #0e1217;
  color: #e1e7ef;
  display: flex;
  flex-direction: column;
}

/* Main Layout */
.news-layout {
  display: flex;
  flex: 1;
  position: relative;
}

/* News Body */
.news-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-left: 240px; /* Ancho del sidebar */
  height: calc(100vh - 57px); /* Altura fija = viewport - header */
  max-height: calc(100vh - 57px);
  background: #0e1217;
  transition: margin-left 0.3s ease;
  overflow: hidden; /* Evitar scroll del contenedor */
}

.loading-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
.news-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: 80px; /* Espacio para el footer fijo */
  height: 100%; /* Forzar altura máxima */
  max-height: calc(100vh - 57px); /* Altura viewport menos header */
}

.news-main__container {
  padding: 24px 20px 0;
  display: flex;
  flex-direction: column;
}

.news-content {
  padding-right: 8px;
}

/* Líderes en IA: tags con icono estilo "historias" */
.company-tags {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 16px;
  padding: 6px 14px;
  background: #1c2127;
  border: 1px solid #2d333b;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.company-tags__label {
  font-size: 12px;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
  padding-right: 14px;
  border-right: 1px solid #2d333b;
}

.company-tags__list {
  display: flex;
  gap: 10px;
  min-width: 0;
  overflow-x: auto;
  scrollbar-width: none;
  padding: 3px; /* deja espacio al scale(1.08) del hover sin recortarlo */
}

.company-tags__list::-webkit-scrollbar {
  display: none;
}

.company-tag {
  position: relative;
  width: 36px;
  height: 36px;
  padding: 2px;
  border-radius: 50%;
  background: #3a414b;
  flex-shrink: 0;
  transition: transform 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease;
}

.company-tag--highlight {
  background: linear-gradient(45deg, #f58529, #dd2a7b, #8134af);
}

.company-tag img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 2px solid #1c2127;
  background: white;
  object-fit: cover;
  display: block;
}

.company-tag:hover,
.company-tag:focus-visible {
  transform: scale(1.08);
  filter: brightness(1.15);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.45);
  outline: none;
}

@media (max-width: 480px) {
  .company-tags {
    gap: 10px;
    padding: 6px 10px;
  }

  .company-tags__label {
    display: none;
  }
}

/* Scrollbar para news-main */
.news-main::-webkit-scrollbar {
  width: 6px;
}
.news-main::-webkit-scrollbar-track {
  background: #1c2127;
  border-radius: 3px;
}
.news-main::-webkit-scrollbar-thumb {
  background: #2d333b;
  border-radius: 3px;
}
.news-main::-webkit-scrollbar-thumb:hover {
  background: #4a9eff;
}
.error-message {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 12px;
  margin-bottom: 20px;
}
.error-icon {
  color: #ef4444;
  flex-shrink: 0;
}
.error-content h4 {
  margin: 0 0 4px 0;
  font-size: 16px;
  font-weight: 600;
  color: #fca5a5;
}
.error-content p {
  margin: 0;
  font-size: 14px;
  color: #fca5a5;
}
/* Encabezado + filtros fijos - Dark Theme */
.section-header-fixed {
  flex-shrink: 0;
  margin-bottom: 16px;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 16px;
  margin-bottom: 12px;
  border-bottom: 1px solid #2d333b;
}

.section-header__icon {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.section-header__icon img {
  filter: brightness(0) invert(1);
  opacity: 0.9;
}

.section-header__main {
  flex: 1;
  min-width: 0;
}

.section-header__top {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.section-title {
  font-size: 22px;
  font-weight: 700;
  margin: 0;
  color: #e1e7ef;
  line-height: 1.2;
}

.section-description {
  font-size: 13px;
  color: #7d8694;
  margin: 4px 0 0 0;
  font-weight: 500;
}

.section-count {
  font-size: 13px;
  font-weight: 500;
  color: #7d8694;
  flex-shrink: 0;
}

/* Fila que agrupa los botones de filtro */
.filter-actions-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-left: auto;
}

/* Botón de filtros (colapsados por defecto en todas las resoluciones) */
.filters-toggle-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #1c2127;
  border: 1px solid #2d333b;
  border-radius: 8px;
  color: #a0aec0;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: inherit;
}

.filters-toggle-btn:hover,
.filters-toggle-btn--active {
  background: #262c34;
  border-color: #4a9eff;
  color: #e1e7ef;
}

/* Destacados: dorado para distinguirlo de los filtros */
.featured-btn {
  border-color: rgba(245, 185, 66, 0.4);
  color: #f5b942;
}

.featured-btn:hover {
  background: rgba(245, 185, 66, 0.1);
  border-color: #f5b942;
  color: #f5b942;
}

.featured-btn--active,
.featured-btn--active:hover {
  background: #f5b942;
  border-color: #f5b942;
  color: #1c2127;
}

/* Filtros de categorías: ocultos hasta que se abren, en una sola fila con scroll */
.categories-filter {
  display: none;
  margin-top: 12px;
}

.categories-filter--visible {
  display: block;
}

.categories-tags {
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  gap: 8px;
  padding: 4px 2px 8px;
}

.categories-tags::-webkit-scrollbar {
  height: 4px;
}

.categories-tags::-webkit-scrollbar-thumb {
  background: #2d333b;
  border-radius: 2px;
}

.category-tag {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #1c2127;
  border: 1px solid #2d333b;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  color: #a0aec0;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  font-family: inherit;
}
.category-tag:hover {
  background: #262c34;
  border-color: #4a5568;
  color: #e1e7ef;
}
.category-tag.active {
  background: rgba(74, 158, 255, 0.15);
  border-color: #4a9eff;
  color: #4a9eff;
}
.category-tag.active:hover {
  background: rgba(74, 158, 255, 0.22);
  border-color: #4a9eff;
}
.category-tag-label {
  font-weight: 600;
}
.category-tag-count {
  font-size: 11px;
  font-weight: 500;
  opacity: 0.65;
}
.category-tag-count::before {
  content: '·';
  margin-right: 6px;
}

/* 🆕 NUEVOS ESTILOS PARA CARGA MANUAL */
.loading-more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 32px 20px;
  color: #7d8694;
  font-size: 14px;
  font-weight: 600;
}
.loading-more .loading-spinner {
  border-color: rgba(74, 158, 255, 0.3);
  border-top-color: #4a9eff;
}

/* Botón Cargar Más */
.load-more-container {
  display: flex;
  justify-content: center;
  padding: 32px 20px;
}
.load-more-btn {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 32px;
  background: linear-gradient(135deg, #4a9eff, #7b68ee);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 16px rgba(74, 158, 255, 0.3);
}
.load-more-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(74, 158, 255, 0.4);
}
.load-more-btn:active {
  transform: translateY(0);
}
.load-more-btn svg {
  flex-shrink: 0;
  animation: bounce 2s infinite;
}
@keyframes bounce {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(4px);
  }
}

.end-message {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 32px 20px;
  color: #34d399;
  font-size: 14px;
  font-weight: 600;
  background: rgba(52, 211, 153, 0.1);
  border: 1px solid rgba(52, 211, 153, 0.3);
  border-radius: 12px;
  margin-top: 20px;
}

/* Botón scroll to top - Estilo ClickUp */
.scroll-to-top {
  position: fixed;
  bottom: 100px; /* Por encima del footer */
  right: 30px;
  width: 56px;
  height: 56px;
  background: white;
  color: black;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 16px rgba(74, 158, 255, 0.5);
  transition: all 0.3s ease;
  z-index: 300;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transform: scale(0.8);
}

.scroll-to-top--visible {
  opacity: 1 !important;
  visibility: visible !important;
  pointer-events: auto !important;
  transform: scale(1) !important;
}

.scroll-to-top--visible:hover {
  transform: scale(1.1) translateY(-3px) !important;
  box-shadow: 0 6px 20px rgba(74, 158, 255, 0.6);
}

.scroll-to-top--visible:active {
  transform: scale(0.95) !important;
}

.scroll-to-top svg {
  width: 24px;
  height: 24px;
  stroke-width: 2.5;
}

/* Mobile Overlay */
.mobile-overlay {
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 400;
  backdrop-filter: blur(4px);
}

/* Sidebar mobile visible */
.sidebar--mobile-visible {
  left: 0 !important;
}

.scroll-to-top svg {
  width: 24px;
  height: 24px;
  stroke-width: 2.5;
}

@media (max-width: 768px) {
  .mobile-overlay {
    display: block;
  }

  .news-body {
    margin-left: 0; /* Sin margen en mobile */
  }

  .news-main {
    padding-bottom: 60px; /* Menos padding en mobile */
  }

  /* Solo iconos en los botones de filtro */
  .filters-toggle-btn {
    padding: 8px;
  }
  .filters-toggle-btn span {
    display: none;
  }
  .section-header {
    gap: 10px;
    padding-bottom: 12px;
  }
  .section-header__icon {
    width: 20px;
    height: 20px;
  }
  .section-title {
    font-size: 18px;
  }
  .section-description {
    display: none;
  }
  .categories-tags {
    gap: 6px;
  }
  .category-tag {
    padding: 5px 10px;
    font-size: 11px;
  }
  .category-tag-count {
    font-size: 10px;
  }
}
@media (max-width: 480px) {
  .news-main__container {
    padding: 16px 12px 0;
  }
  .categories-tags {
    gap: 4px;
  }
  .category-tag {
    padding: 4px 8px;
    font-size: 10px;
  }
  .category-tag-count {
    font-size: 9px;
  }
  .scroll-to-top {
    bottom: 70px;
    right: 16px;
    width: 44px;
    height: 44px;
  }

  .scroll-to-top svg {
    width: 20px;
    height: 20px;
  }
}
</style>